import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import {
  Box,
  Center,
  SimpleGrid,
  HStack,
  Stack,
  Skeleton,
  Text,
  Flex,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormHelperText,
  InputGroup,
  InputLeftElement,
  FormErrorMessage,
  Input,
  Button,
  Circle,
  SkeletonText,
  useToast,
  useDisclosure,
  useColorModeValue,
} from "@chakra-ui/react";

import VCard from "views/user/virtualcard/components/VCard";
import Paginate from "components/navigation/Paginate.js";
import VirtualCardInfo from "views/user/virtualcard/components/VirtualCardInfo";
import Transactions from "views/user/virtualcard/components/Transactions";
import Card from "components/card/Card.js";
import CardBg from "assets/images/cardbg.png";
import { FiPlusCircle, FiEye, FiArrowUp, FiArrowDown } from "react-icons/fi";

export default function VirtualCard() {
  const userState = useSelector((state) => state?.user?.value);
  const token = userState.hasOwnProperty("token") ? userState.token : null;
  const toast = useToast();

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const [userLoading, setUserLoading] = useState(true);
  const [cardLoading, setCardLoading] = useState(true);
  const [fundCardLoading, setFundCardLoading] = useState(false);
  const [withdrawCardLoading, setWithdrawCardLoading] = useState(false);
  const [walletLoading, setWalletLoading] = useState(true);
  const [transactionLoading, setTransactionLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);

  const [pageCount, setPageCount] = useState(0);
  const [sortOrder, setSortOrder] = useState("desc");
  const [from, setFrom] = useState(0);
  const [limit, setLimit] = useState(10);
  const [orderBy, setOrderBy] = useState("createdAt");

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isWithdrawOpen,
    onOpen: onWithdrawOpen,
    onClose: onWithdrawClose,
  } = useDisclosure();

  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [amount, setAmount] = useState(0);
  const [nairaAmount, setNairaAmount] = useState("0.00");
  const [sellError, setSellError] = useState(false);
  const [sellErrorMessage, setSellErrorMessage] = useState("");
  const [sellNairaAmount, setSellNairaAmount] = useState("0.00");

  const [cardTransactionLoading, setCardTransactionLoading] = useState(true);
  const [settings, setSettings] = useState({});

  const [viewCard, setViewCard] = useState(false);
  const [card, setCard] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [rate, setRate] = useState(null);
  const [user, setUser] = useState(null);

  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const brandColor = useColorModeValue("brand.500", "white");
  const textColorSecondary = useColorModeValue("gray.800", "white");

  const checkIfSubscriptionExpired = async (currentSubscriptionExpiry) => {
    const isoDate1 = DateTime.now().toISO();
    const isoDate2 = currentSubscriptionExpiry;
    // Parse the ISO dates into Luxon DateTime objects
    const date1 = DateTime.fromISO(isoDate1);
    const date2 = DateTime.fromISO(isoDate2);
    // Compare the two dates using the Luxon isAfter method
    return date1.isAfter(date2);
  };

  const getUser = async () => {
    try {
      setUserLoading(true);
      const { data: response } = await axios.get(SERVER_URL + "/user/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUserLoading(false);
      if (response.error) {
        toast({
          title: response.error || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (!response.success) {
        toast({
          title: response.message || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (response.success) {
        setUser(response.data);
      }
    } catch (error) {
      toast({
        title: error.message || "An error occured",
        status: "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
      setUserLoading(false);
    }
  };

  const getVirtualCard = async () => {
    try {
      setCardLoading(true);
      const { data: response } = await axios.get(
        SERVER_URL + "/wallet/virtualcard",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCardLoading(false);
      if (response.error) {
        toast({
          title: response.error || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (!response.success) {
        toast({
          title: response.message || "An error occured",
          status: "info",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (response.success) {
        setCard(response.data);
      }
    } catch (error) {
      toast({
        title: error.message || "An error occured",
        status: "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
      setCardLoading(false);
    }
  };

  const freezeVirtualCard = async () => {
    try {
      setCardLoading(true);
      const { data: response } = await axios.get(
        SERVER_URL + "/wallet/freezeVirtualCard",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCardLoading(false);
      if (response.error) {
        toast({
          title: response.error || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (!response.success) {
        toast({
          title: response.message || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (response.success) {
        toast({
          title: response.message,
          status: "success",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        getVirtualCard();
      }
    } catch (error) {
      toast({
        title: error.message || "An error occured",
        status: "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
      setCardLoading(false);
    }
  };

  const unfreezeVirtualCard = async () => {
    try {
      setCardLoading(true);
      const { data: response } = await axios.get(
        SERVER_URL + "/wallet/unfreezeVirtualCard",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCardLoading(false);
      if (response.error) {
        toast({
          title: response.error || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (!response.success) {
        toast({
          title: response.message || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (response.success) {
        toast({
          title: response.message,
          status: "success",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        getVirtualCard();
      }
    } catch (error) {
      toast({
        title: error.message || "An error occured",
        status: "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
      setCardLoading(false);
    }
  };

  const getWallet = async () => {
    try {
      setWalletLoading(true);
      const { data: response } = await axios.get(SERVER_URL + "/wallet/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setWalletLoading(false);
      if (response.error) {
        toast({
          title: response.error || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (!response.success) {
        toast({
          title: response.message || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (response.success) {
        setWallet(response.data);
      }
    } catch (error) {
      toast({
        title: error.message || "An error occured",
        status: "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
      setWalletLoading(false);
    }
  };

  const createVirtualCard = async () => {
    try {
      setCardLoading(true);
      const { data: response } = await axios.get(
        SERVER_URL + "/wallet/createVirtualCard",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCardLoading(false);
      if (response.error) {
        toast({
          title: response.error || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (!response.success) {
        toast({
          title: response.message || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (response.success) {
        toast({
          title: "Virtual account has been issued successfuly",
          status: "success",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        getVirtualCard();
      }
    } catch (error) {
      toast({
        title: error.message || "An error occured",
        status: "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
      setCardLoading(false);
    }
  };

  const getSettings = async () => {
    try {
      setCardTransactionLoading(true);
      const { data: response } = await axios.get(SERVER_URL + "/settings", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setCardTransactionLoading(false);
      if (response.error) {
        toast({
          title: response.error || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (!response.success) {
        toast({
          title: response.message || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (response.success) {
        setSettings(response.data);
        return;
      }
    } catch (error) {
      setCardTransactionLoading(false);
      toast({
        title: error.message || "An error occured",
        status: "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
    }
  };

  const addFunds = async () => {
    try {
      if (nairaAmount >= wallet.balance) {
        toast({
          title: "You don't have sufficient balance",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      setFundCardLoading(true);
      const { data: response } = await axios.post(
        SERVER_URL + "/wallet/addfunds",
        { amount: amount },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.error) {
        toast({
          title: response.error || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
      }
      if (!response.success) {
        toast({
          title: response.message || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
      }
      if (response.success) {
        toast({
          title: response.message,
          status: "success",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        onClose();
        getWallet();
        getVirtualCard();
      }
      setFundCardLoading(false);
    } catch (error) {
      toast({
        title: error.message || "An error occured",
        status: "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
      setFundCardLoading(false);
    }
  };

  const withdrawFunds = async () => {
    try {
      if (amount < 1) {
        toast({
          title: "You can withdraw $1 and above",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      setWithdrawCardLoading(true);
      const { data: response } = await axios.post(
        SERVER_URL + "/wallet/withdrawfunds",
        { amount: amount },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setWithdrawCardLoading(false);
      if (response.error) {
        toast({
          title: response.error || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
      }
      if (!response.success) {
        toast({
          title: response.message || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
      }
      if (response.success) {
        toast({
          title: response.message,
          status: "success",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        onWithdrawClose();
        getWallet();
        getVirtualCard();
      }
    } catch (error) {
      toast({
        title: error.message || "An error occured",
        status: "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
      setWithdrawCardLoading(false);
    }
  };

  const getTransactions = async () => {
    try {
      const { data: response } = await axios.get(
        SERVER_URL +
          "/cardtransaction?from=" +
          from +
          "&limit=" +
          limit +
          "&orderBy=" +
          orderBy +
          "&sortOrder=" +
          sortOrder,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.error) {
        toast({
          title: response.error || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (!response.success) {
        toast({
          title: response.message || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (response.success) {
        setPageCount(response.data.count / limit);
        setTransactions(response.data.transactions);
      }
      setTransactionLoading(false);
    } catch (error) {
      toast({
        title: error.message || "An error occured",
        status: "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    getUser();
    getVirtualCard();
    getWallet();
    getSettings();
    getTransactions();
  }, []);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size={"md"}>
        <ModalOverlay />
        <ModalContent color={textColorSecondary}>
          <ModalHeader>Add Fund</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Card align="center">
              <Stack
                spacing={4}
                w={"full"}
                maxW={"md"}
                bg={useColorModeValue("white", "gray.700")}
                p={3}
              >
                <FormControl isInvalid={error}>
                  <FormLabel fontWeight="bold" color={textColorSecondary}>
                    Dollar Amount
                  </FormLabel>
                  <InputGroup>
                    <InputLeftElement
                      pointerEvents="none"
                      color="gray.300"
                      fontSize="1.2em"
                      children="$"
                    />
                    <Input
                      placeholder="Enter Dollar amount"
                      isInvalid={error}
                      borderRadius="20"
                      errorBorderColor="crimson"
                      onChange={(e) => {
                        const nairaAmount = e.target.value * settings.sellRate;
                        setNairaAmount(nairaAmount.toFixed(2));
                        setAmount(e.target.value);
                        if (nairaAmount > wallet.balance) {
                          setError(true);
                          setErrorMessage("Insufficient balance");
                        }
                        /* else if (
                          //if subscription exipred and on low balance
                          card !== null &&
                          checkIfSubscriptionExpired(card.subscriptionExpiry) &&
                          parseFloat(nairaAmount) +
                            parseFloat(settings.sellRate) >
                            wallet.balance
                        ) {
                          setError(true);
                          setErrorMessage("Insufficient balance");
                        }*/
                        else {
                          setError(false);
                        }
                      }}
                    />
                  </InputGroup>
                </FormControl>

                <FormControl isInvalid={error}>
                  <FormLabel fontWeight="bold" color={textColorSecondary}>
                    Naira Amount
                  </FormLabel>
                  <Input
                    type="text"
                    value={nairaAmount}
                    borderRadius="20"
                    precision={2}
                    readOnly
                  />
                  {/*card !== null &&
                    checkIfSubscriptionExpired(card.subscriptionExpiry) && (
                      <FormHelperText>
                        <b>Monthly Maintenance Fee:</b> NGN {settings.sellRate},{" "}
                        <b>Total:</b>{" "}
                        {parseFloat(nairaAmount) +
                          parseFloat(settings.sellRate)}{" "}
                      </FormHelperText>
                        ) */ }
                  {error && (
                    <Center>
                    <FormErrorMessage textAlign="center">
                      Insufficient wallet balance
                    </FormErrorMessage>
                    </Center>
                  )}
                </FormControl>

                <Button
                  isLoading={fundCardLoading}
                  loadingText={"Please wait.."}
                  disabled={error}
                  bg={"blue.400"}
                  color={"white"}
                  w="full"
                  borderRadius="20"
                  _hover={{
                    bg: "blue.500",
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    addFunds();
                  }}
                >
                  Add Funds
                </Button>
              </Stack>
            </Card>
          </ModalBody>
          <ModalFooter></ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isWithdrawOpen} onClose={onWithdrawClose} size={"md"}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Withdraw Fund</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Card align="center">
              <Stack
                spacing={4}
                w={"full"}
                maxW={"md"}
                bg={useColorModeValue("white", "gray.700")}
                p={3}
              >
                <FormControl isInvalid={sellError}>
                  <FormLabel fontWeight="bold" color={textColorSecondary}>
                    Dollar Amount
                  </FormLabel>
                  <InputGroup>
                    <InputLeftElement
                      pointerEvents="none"
                      color="gray.300"
                      fontSize="1.2em"
                      children="$"
                    />
                    <Input
                      placeholder="Enter Dollar amount"
                      isInvalid={error}
                      errorBorderColor="crimson"
                      onChange={(e) => {
                        const sellNairaAmount =
                          e.target.value * settings.buyRate;
                        setSellNairaAmount(sellNairaAmount.toFixed(2));
                        setAmount(e.target.value);
                        if (e.target.value > wallet.balance) {
                          //change to card balance
                          setSellError(true);
                          setSellErrorMessage("Insufficient balance on card");
                        } else {
                          setSellError(false);
                        }
                      }}
                    />
                  </InputGroup>
                  {sellError && (
                    <FormErrorMessage>
                      Insufficient balance on card
                    </FormErrorMessage>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel fontWeight="bold" color={textColorSecondary}>
                    Naira Amount
                  </FormLabel>
                  <Input
                    type="text"
                    value={sellNairaAmount}
                    borderRadius="20"
                    readOnly
                  />
                </FormControl>

                <Button
                  isLoading={withdrawCardLoading}
                  loadingText={"Please Wait.."}
                  bg={"blue.400"}
                  color={"white"}
                  w="full"
                  borderRadius="20"
                  _hover={{
                    bg: "blue.500",
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    withdrawFunds();
                  }}
                >
                  Withdraw
                </Button>
              </Stack>
            </Card>
          </ModalBody>
          <ModalFooter></ModalFooter>
        </ModalContent>
      </Modal>

      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <SimpleGrid
          columns={{ base: 1, sm: 1, md: 2, lg: 2, xl: 2 }}
          gap="10px"
        >
          <Box p="0">
            <HStack
              columns={{ base: 3, sm: 3, md: 3, lg: 3, xl: 3 }}
              gap="0"
              padding="0px"
            >
              <Box w="5%" position="relative">
                <Card
                  align="center"
                  p="15px"
                  backgroundImage={`url(${CardBg})`}
                  backgroundPosition="center"
                  backgroundRepeat="no-repeat"
                  backgroundSize="cover"
                  backgroundColor="#0A369D"
                  h="160px"
                  position="absolute"
                  top="-80px"
                  left="15px"
                ></Card>
              </Box>
              <Box w="90%" zIndex="2">
                <VCard card={card} viewCard={viewCard} />
              </Box>
              <Box w="5%" position="relative">
                <Card
                  align="center"
                  p="15px"
                  backgroundImage={`url(${CardBg})`}
                  backgroundPosition="center"
                  backgroundRepeat="no-repeat"
                  backgroundSize="cover"
                  backgroundColor="#001C55"
                  h="160px"
                  position="absolute"
                  top="-80px"
                  right="15px"
                ></Card>
              </Box>
            </HStack>
            {card !== null && (
              <Card align="center" p="20px">
                <SimpleGrid columns={{ base: 1, md: "2" }}>
                  <Box textAlign="start" mt={{ base: 2, sm: 2 }}>
                    <Flex>
                      <Icon as={FiEye} color={brandColor} w="20px" h="20px" />
                      <Text
                        fontSize={{
                          base: "xs",
                          sm: "sm",
                          md: "md",
                          lg: "sm",
                        }}
                        my={{ base: "auto", "2xl": "10px" }}
                        color={brandColor}
                        onClick={(e) => {
                          if (viewCard) {
                            setViewCard(false);
                          } else {
                            setViewCard(true);
                          }
                        }}
                      >
                        &nbsp;
                        {viewCard ? "Hide Card Details" : "View Card Details"}
                      </Text>
                      {card !== null && viewCard && (
                        <Text
                          fontSize={{
                            base: "xs",
                            sm: "sm",
                            md: "md",
                            lg: "sm",
                          }}
                          my={{ base: "auto", "2xl": "10px" }}
                          color={brandColor}
                        >
                          {card.cardData.bilingAddress}
                        </Text>
                      )}
                    </Flex>
                  </Box>
                  <Box textAlign="start" mt={{ base: 2, sm: 2 }}>
                    <Flex>
                      <Icon
                        as={FiPlusCircle}
                        color={brandColor}
                        w="20px"
                        h="20px"
                      />
                      {card !== null && !card.card.frozen && (
                        <Text
                          fontSize={{
                            base: "xs",
                            sm: "sm",
                            md: "md",
                            lg: "sm",
                          }}
                          my={{ base: "auto", "2xl": "10px" }}
                          color={brandColor}
                          onClick={freezeVirtualCard}
                        >
                          &nbsp;Freeze Card
                        </Text>
                      )}

                      {card !== null && card.card.frozen && (
                        <Text
                          fontSize={{
                            base: "xs",
                            sm: "sm",
                            md: "md",
                            lg: "sm",
                          }}
                          my={{ base: "auto", "2xl": "10px" }}
                          color={brandColor}
                          onClick={unfreezeVirtualCard}
                        >
                          &nbsp;Unfreeze Card
                        </Text>
                      )}
                    </Flex>
                  </Box>
                </SimpleGrid>
              </Card>
            )}
          </Box>
          <Box
            p={{ base: "2px", lg: "2px" }}
            pl={{ base: "0px", sm: "0px", md: "10px", lg: "10px" }}
            borderLeftWidth={{ base: "0px", sm: "0px", md: "1px", lg: "1px" }}
            borderLeftColor={"#ccc"}
            borderLeftStyle="solid"
          >
            {userLoading || cardLoading || walletLoading ? (
              <LoadingCard />
            ) : card !== null ? (
              <>
                <Card align="center" p="20px" backgroundColor="#051d6d">
                  <HStack spacing="0" h="170px">
                    <Box
                      w="50%"
                      h="100%"
                      backgroundColor="#051d6d"
                      p="5"
                      borderLeftRadius="20px"
                    >
                      <HStack>
                        <Box w="70%">
                          <Text
                            color="#fff"
                            fontSize="md"
                            my={{ base: "auto", "2xl": "10px" }}
                            mx="auto"
                            fontWeight="bold"
                            textAlign="start"
                          >
                            Top Up
                          </Text>
                          <Text
                            color={"white"}
                            fontSize={{
                              base: "xs",
                              sm: "sm",
                              md: "md",
                              lg: "sm",
                            }}
                            my={{ base: "auto", "2xl": "10px" }}
                            mx="auto"
                            textAlign="start"
                          >
                            Add funds to your virtual debit card from your
                            Swaptrex account
                          </Text>
                        </Box>
                        <Box w="30%" onClick={onOpen}>
                          <Circle size="40px" bg="#fff" color="white">
                            <Icon
                              as={FiArrowUp}
                              w="25px"
                              h="25px"
                              color="blue"
                            />
                          </Circle>
                        </Box>
                      </HStack>
                    </Box>

                    <Box
                      w="50%"
                      h="100%"
                      backgroundColor="#051d6d"
                      p="5"
                      borderRightRadius="20px"
                    >
                      <HStack>
                        <Box w="70%">
                          <Text
                            color="#fff"
                            fontSize="md"
                            fontWeight="bold"
                            my={{ base: "auto", "2xl": "10px" }}
                            mx="auto"
                            textAlign="start"
                          >
                            Withdraw
                          </Text>
                          <Text
                            color={"white"}
                            fontSize={{
                              base: "xs",
                              sm: "sm",
                              md: "md",
                              lg: "sm",
                            }}
                            my={{ base: "auto", "2xl": "10px" }}
                            mx="auto"
                            textAlign="start"
                          >
                            Remove funds to your virtual debit card to your
                            Swaptrex account
                          </Text>
                        </Box>
                        <Box w="30%" onClick={onWithdrawOpen}>
                          <Circle size="40px" bg="white" color="white">
                            <Icon
                              as={FiArrowDown}
                              w="25px"
                              h="25px"
                              color="blue"
                            />
                          </Circle>
                        </Box>
                      </HStack>
                    </Box>
                  </HStack>
                </Card>
              </>
            ) : (
              <VirtualCardInfo
                user={user}
                getUser={getUser}
                cardLoading={cardLoading}
                createVirtualCard={createVirtualCard}
              />
            )}
          </Box>
        </SimpleGrid>

        <Box mt="5">
          {transactionLoading ? (
            <LoadingTransaction />
          ) : (
            <Transactions transactions={transactions} />
          )}
          {transactions.length > 0 && (
            <Box width="100%" mt="5">
              <Paginate
                pageCount={pageCount}
                setFrom={setFrom}
                getRecords={getTransactions}
              />
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}

const LoadingCard = () => {
  return (
    <Card align="center" p="20px">
      <Stack>
        <Skeleton height="30px" />
        <SkeletonText mt="5" noOfLines={5} spacing="4" />
        <Skeleton height="30px" />
      </Stack>
    </Card>
  );
};

const LoadingTransaction = () => {
  return (
    <>
      <Card align="center" p="5px">
        <Stack>
          <Skeleton height="5px" />
          <SkeletonText mt="2" noOfLines={3} spacing="3" />
          <Skeleton height="5px" />
        </Stack>
      </Card>

      <Card align="center" p="5px" mt="5">
        <Stack>
          <Skeleton height="5px" />
          <SkeletonText mt="2" noOfLines={3} spacing="3" />
          <Skeleton height="5px" />
        </Stack>
      </Card>
    </>
  );
};
